import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, ChevronRight, ShieldCheck, Download, Lock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Toaster } from "../components/ui/toaster";
import { ValuationProvider } from "../components/ValuationDialog";
import Seo from "../components/Seo";
import TermsContent from "../components/TermsContent";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import SignaturePad from "../components/SignaturePad";
import { useToast } from "../hooks/use-toast";
import { LANDLORD_SERVICES } from "../mock";
import { DEFAULT_FEES, rateFor } from "../lib/termsFees";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEL = "h-10 w-full rounded-md border border-[#e5ddd0] bg-white px-3 text-sm text-[#16323f] focus:border-[#35a4a4] focus:outline-none";

function YesNo({ value, onChange, name, options = ["Yes", "No"] }) {
  return (
    <div className="flex gap-2">
      {options.map((o) => (
        <button key={o} type="button" data-testid={`${name}-${o.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => onChange(o)}
          className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
            value === o ? "border-[#35a4a4] bg-[#35a4a4] text-white" : "border-[#e5ddd0] bg-white text-[#5b5450] hover:border-[#35a4a4]"
          }`}>
          {o}
        </button>
      ))}
    </div>
  );
}

const SectionHead = ({ n, title, hint }) => (
  <div className="mt-8 border-t border-[#eee6da] pt-6">
    <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#c9a25e]">Section {n}</p>
    <h3 className="mt-1 font-display text-lg font-600 text-[#16323f]">{title}</h3>
    {hint && <p className="mt-1 text-sm text-[#7a716a]">{hint}</p>}
  </div>
);

function Content() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const { code } = useParams();
  const [link, setLink] = useState(null);
  const [linkState, setLinkState] = useState(code ? "loading" : "none");
  const [form, setForm] = useState({
    service: params.get("service") || "Full Management",
    draft_contract: false, register_deposit: false, check_in_out: false,
    gas_certificate: false, eicr: false, epc: false,
    start_work_waiver: false,
    name: "", email: "", phone: "", owner_address: "", co_owners: "",
    property_address: "", bedrooms: "", furnished: "", parking: "", garden: "", licensing: "", property_notes: "",
    bank_account_name: "", bank_name: "", sort_code: "", account_number: "",
    signature_name: "", signature_data: "", agreed: false, privacy_consent: false,
    link_code: code || "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [savedId, setSavedId] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fees = link || DEFAULT_FEES;
  const lockedService = link?.service || "";
  const services = lockedService ? LANDLORD_SERVICES.filter((s) => s.name === lockedService) : LANDLORD_SERVICES;

  useEffect(() => {
    if (!code) return;
    axios.get(`${API}/terms-links/${code}`)
      .then((r) => {
        setLink(r.data);
        setLinkState("ok");
        if (r.data.service) set("service", r.data.service);
        if (r.data.landlord_name) set("name", r.data.landlord_name);
      })
      .catch(() => setLinkState("invalid"));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.owner_address || !form.property_address || !form.signature_name) {
      toast({ title: "Missing details", description: "Please complete your name, email, home address, property address and signature name.", variant: "destructive" });
      return;
    }
    if (!form.bank_account_name || !form.sort_code || !form.account_number) {
      toast({ title: "Rent payment details needed", description: "Please tell us where your rent should be paid (account name, sort code and account number).", variant: "destructive" });
      return;
    }
    if (!form.agreed) {
      toast({ title: "Please confirm", description: "You must agree to the Terms of Business to continue.", variant: "destructive" });
      return;
    }
    if (!form.privacy_consent) {
      toast({ title: "Please confirm", description: "Please confirm you're happy for us to process your details as described.", variant: "destructive" });
      return;
    }
    if (!form.signature_data) {
      toast({ title: "Signature required", description: "Please sign in the box to accept electronically.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/terms-acceptances`, form);
      setSavedId(res.data?.id || "");
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast({ title: "Terms accepted", description: "Thank you \u2014 a copy has been recorded." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <Seo title="Terms of Business | New Space Lettings, Hackney" description="Review and accept the New Space Terms of Business online — Let Only, Rent Collection and Full Management — with a quick, secure electronic signature." path="/landlords/terms" noindex={!!code} />
      <Header />

      <section className="relative overflow-hidden bg-[#16323f] pt-32 pb-14 lg:pt-40">
        <div className="mx-auto max-w-[900px] px-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#d8b074]">New Space</p>
          <h1 className="mt-3 font-display text-4xl font-700 uppercase tracking-wide text-white lg:text-5xl">
            Terms of Business
          </h1>
          {linkState === "ok" ? (
            <div className="mx-auto mt-4 flex max-w-md flex-col items-center gap-1 rounded-2xl border border-[#d8b074]/40 bg-white/5 px-5 py-3 text-sm text-white/90 sm:inline-flex sm:flex-row sm:gap-2 sm:rounded-full sm:py-2" data-testid="private-terms-banner">
              <span className="inline-flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0"><Lock size={14} className="mr-0.5 text-[#d8b074]" />
              <span>Private terms prepared for</span> <strong className="font-semibold text-white">{link.landlord_name || "you"}</strong></span>
              {lockedService && <span className="text-white/60"><span className="hidden sm:inline">&middot; </span>{lockedService} at {rateFor(fees, lockedService)}</span>}
            </div>
          ) : (
            <p className="mt-3 text-white/80">Let Only &middot; Rent Collection &middot; Full Management</p>
          )}
        </div>
      </section>

      <section className="bg-[#faf7f0] py-14 lg:py-20">
        <div className="mx-auto max-w-[900px] px-5">
          {linkState === "loading" ? (
            <div className="flex justify-center py-20" data-testid="private-terms-loading"><Loader2 className="animate-spin text-[#35a4a4]" size={32} /></div>
          ) : linkState === "invalid" ? (
            <div className="rounded-2xl border border-[#e5ddd0] bg-white p-10 text-center shadow-lg" data-testid="private-terms-invalid">
              <h2 className="font-display text-2xl font-600 text-[#16323f]">This link isn't valid</h2>
              <p className="mx-auto mt-3 max-w-md text-sm text-[#5b5450]">The private terms link you followed has expired or doesn't exist. Please contact us on 020 7275 2040 and we'll send you a fresh one.</p>
              <button onClick={() => navigate("/landlords/terms")} data-testid="go-standard-terms-button"
                className="mt-7 rounded-full bg-[#16323f] px-9 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#ec008c]">
                View standard terms
              </button>
            </div>
          ) : done ? (
            <div className="rounded-2xl border border-[#e5ddd0] bg-white p-10 text-center shadow-lg">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#35a4a4] text-white">
                <Check size={34} />
              </div>
              <h2 className="mt-5 font-display text-2xl font-600 text-[#16323f]">Terms accepted</h2>
              <p className="mx-auto mt-3 max-w-md text-sm text-[#5b5450]">
                Thank you for accepting the New Space Terms of Business for the <strong>{form.service}</strong> service.
                We've recorded your acceptance and our team will be in touch to arrange the next steps.
              </p>
              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                {savedId && (
                  <a href={`${API}/terms-acceptances/${savedId}/pdf`} target="_blank" rel="noopener noreferrer"
                    data-testid="download-terms-pdf-button"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#35a4a4] px-9 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#2b8686]">
                    <Download size={16} /> Download my copy
                  </a>
                )}
                <button onClick={() => navigate("/landlords")} data-testid="back-to-landlords-button"
                  className="rounded-full bg-[#16323f] px-9 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#ec008c]">
                  Back to Landlords
                </button>
              </div>
            </div>
          ) : (
            <>
              <TermsContent fees={fees} lockedService={lockedService} />

              {/* Form */}
              <form onSubmit={submit} className="mt-8 rounded-2xl border border-[#e5ddd0] bg-white p-7 shadow-sm lg:p-9" id="accept">
                <h2 className="font-display text-xl font-600 uppercase tracking-wide text-[#16323f]">Accept &amp; sign online</h2>
                <p className="mt-1 text-sm text-[#7a716a]">Takes about ten minutes. Please complete every detail below so we have everything we need to get started &mdash; nothing is taken over the phone.</p>

                <div className="mt-6 space-y-2">
                  <Label>{lockedService ? "Your service" : "Choose your service *"}</Label>
                  <RadioGroup value={form.service} onValueChange={(v) => !lockedService && set("service", v)} className={`grid gap-3 ${lockedService ? "sm:max-w-sm" : "sm:grid-cols-3"}`}>
                    {services.map((s) => {
                      const rate = rateFor(fees, s.name);
                      return (
                      <label key={s.name} htmlFor={`svc-${s.name}`}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition-colors ${
                          form.service === s.name ? "border-[#ec008c] bg-[#ec008c]/5" : "border-[#e5ddd0]"
                        }`}>
                        <RadioGroupItem value={s.name} id={`svc-${s.name}`} />
                        <span>
                          <span className="block text-sm font-semibold text-[#16323f]">{s.name}</span>
                          <span className="block text-xs text-[#7a716a]">{rate}</span>
                        </span>
                      </label>
                      );
                    })}
                  </RadioGroup>
                </div>

                <div className="mt-6 space-y-2">
                  <Label>Add-ons — please tick anything you'd also like us to handle</Label>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {[
                      ["draft_contract", "Draft the tenancy contract", "", ""],
                      ["register_deposit", "Register the deposit", "", ""],
                      ["check_in_out", "Arrange check-in & check-out", "", "See pricing in Section 13"],
                      ["gas_certificate", "Arrange Gas Safety Certificate", "£95 + VAT", ""],
                      ["eicr", "Arrange EICR (electrical report)", "£225 + VAT", ""],
                      ["epc", "Arrange EPC (energy certificate)", "£90 + VAT", ""],
                    ].map(([key, label, price, note]) => (
                      <label key={key} htmlFor={`addon-${key}`}
                        className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${
                          form[key] ? "border-[#35a4a4] bg-[#35a4a4]/5" : "border-[#e5ddd0]"
                        }`}>
                        <Checkbox id={`addon-${key}`} data-testid={`addon-${key.replace(/_/g, "-")}`}
                          checked={form[key]} onCheckedChange={(v) => set(key, !!v)} className="mt-0.5" />
                        <span>
                          <span className="block text-sm font-medium text-[#16323f]">{label}</span>
                          {price && <span className="mt-0.5 block text-xs font-semibold text-[#ec008c]">{price}</span>}
                          {note && <span className="mt-0.5 block text-xs text-[#7a716a]">{note}</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <SectionHead n={1} title="Your details" />
                <div className="mt-4 grid gap-5 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="t-name">Full name *</Label>
                    <Input id="t-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="t-phone">Phone *</Label>
                    <Input id="t-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="020 7275 2040" />
                  </div>
                </div>
                <div className="mt-5 space-y-1.5">
                  <Label htmlFor="t-email">Email *</Label>
                  <Input id="t-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
                </div>
                <div className="mt-5 space-y-1.5">
                  <Label htmlFor="t-owner-addr">Your home address (where you live) *</Label>
                  <Textarea id="t-owner-addr" rows={2} value={form.owner_address} onChange={(e) => set("owner_address", e.target.value)} placeholder="Where you live and where we should send correspondence, including postcode" />
                </div>
                <div className="mt-5 space-y-1.5">
                  <Label htmlFor="t-coowners">Other legal owners (if jointly owned)</Label>
                  <Textarea id="t-coowners" rows={2} value={form.co_owners} onChange={(e) => set("co_owners", e.target.value)} placeholder="Full name and home address of every other legal owner" />
                </div>

                <SectionHead n={2} title="The property" />
                <div className="mt-4 space-y-1.5">
                  <Label htmlFor="t-addr">Property address *</Label>
                  <Input id="t-addr" value={form.property_address} onChange={(e) => set("property_address", e.target.value)} placeholder="1 Kingsland Road, London E8" />
                </div>
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="t-beds">Bedrooms</Label>
                    <select id="t-beds" data-testid="terms-bedrooms" className={SEL} value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)}>
                      <option value="">Select…</option>
                      {["Studio", "1", "2", "3", "4", "5", "6+"].map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="t-furn">Furnished?</Label>
                    <select id="t-furn" data-testid="terms-furnished" className={SEL} value={form.furnished} onChange={(e) => set("furnished", e.target.value)}>
                      <option value="">Select…</option>
                      {["Furnished", "Unfurnished", "Part furnished"].map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Parking space?</Label>
                    <YesNo name="terms-parking" value={form.parking} onChange={(v) => set("parking", v)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Garden / outside space?</Label>
                    <YesNo name="terms-garden" value={form.garden} onChange={(v) => set("garden", v)} />
                  </div>
                </div>
                <div className="mt-5 space-y-1.5">
                  <Label>Is the property subject to any licensing? (e.g. HMO, selective or additional licensing)</Label>
                  <YesNo name="terms-licensing" options={["Yes", "No", "Not sure"]} value={form.licensing} onChange={(v) => set("licensing", v)} />
                </div>
                <div className="mt-5 space-y-1.5">
                  <Label htmlFor="t-notes">Anything else we should know about the property</Label>
                  <Textarea id="t-notes" rows={3} value={form.property_notes} onChange={(e) => set("property_notes", e.target.value)}
                    placeholder="Meter locations, keys and access, parking details, block managing agent, alarm codes — anything that helps us look after it well" />
                </div>

                <SectionHead n={3} title="Where to pay your rent"
                  hint="The UK bank account your rent should be paid into. We need this for every service — for Let Only it's where we send the balance of the first month's rent after our fee." />
                <div className="mt-4 grid gap-5 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="t-acc-name">Account holder name *</Label>
                    <Input id="t-acc-name" value={form.bank_account_name} onChange={(e) => set("bank_account_name", e.target.value)} placeholder="As it appears on the account" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="t-bank">Bank name</Label>
                    <Input id="t-bank" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} placeholder="e.g. Barclays" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="t-sort">Sort code *</Label>
                    <Input id="t-sort" value={form.sort_code} onChange={(e) => set("sort_code", e.target.value)} placeholder="00-00-00" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="t-accnum">Account number *</Label>
                    <Input id="t-accnum" value={form.account_number} onChange={(e) => set("account_number", e.target.value)} placeholder="12345678" />
                  </div>
                </div>
                <div className="mt-3 flex items-start gap-2 text-xs text-[#7a716a]">
                  <ShieldCheck size={14} className="mt-0.5 shrink-0 text-[#35a4a4]" />
                  <span>Sent securely and used only to set up and manage your tenancy payments.</span>
                </div>

                <SectionHead n={4} title="Declarations & signature" />

                <div className="mt-6 flex items-start gap-3 rounded-xl bg-[#f3ede3] p-4">
                  <Checkbox id="t-agree" checked={form.agreed} onCheckedChange={(v) => set("agreed", !!v)} className="mt-0.5" />
                  <Label htmlFor="t-agree" className="text-sm font-normal leading-relaxed text-[#5b5450]">
                    I have read and agree to the New Space Terms of Business (Schedules 1–4), and I warrant that I am
                    the owner of the property or otherwise lawfully entitled to grant a tenancy.
                  </Label>
                </div>

                <div className="mt-3 flex items-start gap-3 rounded-xl bg-[#f3ede3] p-4">
                  <Checkbox id="t-privacy" data-testid="terms-privacy-consent" checked={form.privacy_consent} onCheckedChange={(v) => set("privacy_consent", !!v)} className="mt-0.5" />
                  <Label htmlFor="t-privacy" className="text-sm font-normal leading-relaxed text-[#5b5450]">
                    I understand New Space will use the details I provide to set up and manage the letting of my
                    property, including storing them in its property management system (Street) and sharing them with
                    referencing, compliance and tenancy providers where needed. Details are kept only as long as
                    required for the tenancy and our legal obligations. See our{" "}
                    <a href="https://www.newspaceuk.com/privacy-policy" target="_blank" rel="noopener noreferrer"
                      className="font-semibold text-[#ec008c] underline underline-offset-2">Privacy Policy</a>.
                  </Label>
                </div>

                <div className="mt-3 flex items-start gap-3 rounded-xl border border-[#e5ddd0] p-4">
                  <Checkbox id="t-waiver" data-testid="terms-start-work-waiver" checked={form.start_work_waiver} onCheckedChange={(v) => set("start_work_waiver", !!v)} className="mt-0.5" />
                  <Label htmlFor="t-waiver" className="text-sm font-normal leading-relaxed text-[#5b5450]">
                    (Optional) I'd like New Space to begin work within the 14-day cancellation period. I understand
                    that if I cancel I'll pay for any services already provided, and that the right to cancel is lost
                    once a service has been fully performed.
                  </Label>
                </div>


                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="t-sig">Type your full name (as signature) *</Label>
                    <Input id="t-sig" value={form.signature_name} onChange={(e) => set("signature_name", e.target.value)}
                      placeholder="Jane Smith" className="font-display text-lg" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Draw your signature *</Label>
                    <SignaturePad onChange={(d) => set("signature_data", d)} />
                  </div>
                </div>

                <button type="submit" disabled={loading}
                  className="group mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[#ec008c] py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074] disabled:opacity-70">
                  {loading ? (<><Loader2 size={16} className="animate-spin" /> Submitting...</>) : (
                    <><ShieldCheck size={16} /> Accept terms &amp; sign <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" /></>
                  )}
                </button>
                <p className="mt-3 text-center text-xs text-[#7a716a]">
                  By submitting you accept the Terms electronically, with the same effect as a written signature.
                </p>
              </form>
            </>
          )}
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
}

export default function TermsSign() {
  return (
    <ValuationProvider>
      <Content />
    </ValuationProvider>
  );
}
